import { useEffect, useMemo, useState } from 'react';
import {
  ArrowLeft,
  CalendarClock,
  CalendarDays,
  CheckCircle2,
  ClipboardCheck,
  Clock,
  Globe,
  Hourglass,
  Users,
} from 'lucide-react';
import { apiDashboard } from '../api/client.js';

/**
 * Ticket #49 — écran de détail d'une tuile de l'accueil.
 *
 * Chaque carte du tableau de bord est cliquable et ouvre cet écran, qui ne
 * montre QUE ce que la tuile mesure : la liste des jours ouvrés concernés,
 * avec par défaut le filtre « à compléter » (jours non planifiés pour les
 * tuiles Planification, non saisis pour les tuiles Logtime).
 *
 * Le périmètre (soi / équipe / tout le monde) reprend celui de l'accueil et
 * reste borné par le rôle côté serveur (`GET /api/dashboard/detail`).
 */

const METRICS = {
  schedulingMonth: {
    label: 'Planification (mois complet)',
    Icon: CalendarClock,
    kind: 'scheduling',
    windowKeys: ['schedulingMonthFrom', 'schedulingMonthTo'],
  },
  scheduling: {
    label: 'Planification (reste du mois)',
    Icon: CalendarClock,
    kind: 'scheduling',
    windowKeys: ['schedulingFrom', 'schedulingTo'],
  },
  prevWeek: {
    label: 'Logtime — semaine précédente',
    Icon: ClipboardCheck,
    kind: 'logtime',
    windowKeys: ['prevWeekStart', 'prevWeekEnd'],
  },
  currentWeek: {
    label: 'Logtime — semaine en cours',
    Icon: Hourglass,
    kind: 'logtime',
    windowKeys: ['currentWeekStart', 'currentWeekEnd'],
  },
};

export const DETAIL_METRIC_KEYS = Object.keys(METRICS);

const DAY_NAMES = ['dim.', 'lun.', 'mar.', 'mer.', 'jeu.', 'ven.', 'sam.'];

function fmtDay(iso) {
  const [y, m, d] = iso.split('-').map(Number);
  const date = new Date(Date.UTC(y, m - 1, d));
  return `${DAY_NAMES[date.getUTCDay()]} ${String(d).padStart(2, '0')}/${String(m).padStart(2, '0')}`;
}

function fmtDate(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y.slice(2)}`;
}

function fmtHours(h) {
  if (h === null || h === undefined) return '0';
  return Number.isInteger(h) ? String(h) : h.toFixed(1);
}

const card = {
  background: 'var(--card-bg)',
  border: '1px solid var(--glass-border)',
  borderRadius: 'var(--radius-md)',
  boxShadow: 'var(--shadow-card)',
  padding: '16px 18px',
};

const sectionTitle = {
  margin: 0,
  fontSize: 15,
  fontWeight: 600,
  color: 'var(--text)',
  display: 'flex',
  alignItems: 'center',
  gap: 8,
};

/**
 * Pastille d'un jour : rouge = à compléter, verte = fait, grise = à venir.
 * Retour #49 (gekad) : la pastille est **actionnable** — un clic ouvre
 * directement la saisie des temps (tuiles Logtime) ou le planning (tuiles
 * Planification) sur la semaine du jour, et sur la personne concernée.
 */
function DayChip({ day, kind, onOpen }) {
  const missing = !day.filled && !day.future;
  const color = day.future
    ? 'var(--text-muted)'
    : day.filled
      ? 'var(--success)'
      : 'var(--danger)';
  const detail = day.filled
    ? `${fmtHours(day.hours)}h / ${fmtHours(day.capacityHours)}h`
    : day.future
      ? 'à venir'
      : kind === 'scheduling'
        ? 'non planifié'
        : 'non saisi';
  const action = kind === 'scheduling' ? 'Planifier' : 'Saisir';
  return (
    <button
      type="button"
      onClick={onOpen}
      disabled={!onOpen}
      title={`${fmtDate(day.date)} — ${detail}${onOpen ? ` · ${action}` : ''}`}
      style={{
        font: 'inherit',
        textAlign: 'left',
        cursor: onOpen ? 'pointer' : 'default',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        padding: '8px 10px',
        borderRadius: 'var(--radius-sm)',
        border: `1px solid ${missing ? 'var(--danger)' : 'var(--glass-border)'}`,
        background: 'var(--glass)',
        minWidth: 108,
      }}
    >
      <span style={{ fontSize: 12, color: 'var(--text)', fontWeight: 600 }}>
        {fmtDay(day.date)}
      </span>
      <span style={{ fontSize: 11, color }}>{detail}</span>
      {onOpen ? (
        <span
          style={{
            fontSize: 11,
            color: 'var(--accent)',
            fontWeight: 600,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          {kind === 'scheduling' ? <CalendarDays size={12} /> : <Clock size={12} />}
          {action}
        </span>
      ) : null}
    </button>
  );
}

function DayList({ days, kind, emptyLabel, onOpenDay }) {
  if (!days || days.length === 0) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--success)', fontSize: 13 }}>
        <CheckCircle2 size={15} /> {emptyLabel}
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
      {days.map((d) => (
        <DayChip
          key={d.date}
          day={d}
          kind={kind}
          onOpen={onOpenDay ? () => onOpenDay(d.date) : undefined}
        />
      ))}
    </div>
  );
}

function pill(active, label, Icon, onClick) {
  return (
    <button
      key={label}
      type="button"
      onClick={onClick}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        background: active ? 'var(--accent)' : 'transparent',
        color: active ? '#fff' : 'var(--text)',
        border: '1px solid var(--glass-border)',
        borderRadius: 'var(--radius-pill)',
        padding: '6px 14px',
        fontSize: 13,
        fontWeight: 500,
        cursor: 'pointer',
      }}
    >
      {Icon ? <Icon size={14} /> : null}
      {label}
    </button>
  );
}

export default function DashboardDetailView({
  detailMetric,
  detailScope,
  onNavigate,
  onOpenDay,
  onBack,
}) {
  const metricKey = METRICS[detailMetric] ? detailMetric : 'schedulingMonth';
  const meta = METRICS[metricKey];

  const [scope, setScope] = useState(detailScope || 'self');
  const [onlyMissing, setOnlyMissing] = useState(true);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    apiDashboard
      .detail(metricKey, scope)
      .then((r) => {
        if (!cancelled) setData(r);
      })
      .catch((e) => {
        if (!cancelled) setError(e.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [metricKey, scope]);

  const win = data?.window ?? null;
  const [fromKey, toKey] = meta.windowKeys;
  const periodLabel = win ? `Du ${fmtDate(win[fromKey])} au ${fmtDate(win[toKey])}` : '';

  const keepDays = useMemo(
    () => (days) =>
      (days ?? []).filter((d) => (onlyMissing ? !d.filled && !d.future : true)),
    [onlyMissing],
  );

  const myDays = keepDays(data?.personal?.metric?.days);
  const teamRows = (data?.rows ?? [])
    .map((r) => ({ ...r, days: keepDays(r.metric?.days) }))
    .filter((r) => !onlyMissing || r.days.length > 0);

  const emptyLabel =
    meta.kind === 'scheduling'
      ? 'Aucun jour ouvré non planifié sur la période.'
      : 'Aucun jour ouvré sans saisie sur la période.';

  const targetView = meta.kind === 'scheduling' ? 'schedule' : 'logtime';
  const targetLabel = meta.kind === 'scheduling' ? 'Ouvrir le planning' : 'Ouvrir la saisie des temps';
  const TargetIcon = meta.kind === 'scheduling' ? CalendarDays : Clock;

  const availableScopes = data?.availableScopes ?? ['self'];

  return (
    <div
      style={{
        padding: '24px 32px',
        maxWidth: 1200,
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column',
        gap: 24,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={onBack}
          title="Retour à l'accueil"
          aria-label="Retour à l'accueil"
          style={{
            background: 'transparent',
            color: 'var(--text)',
            border: '1px solid var(--glass-border)',
            borderRadius: 'var(--radius-sm)',
            padding: 6,
            display: 'inline-flex',
            alignItems: 'center',
            cursor: 'pointer',
          }}
        >
          <ArrowLeft size={16} />
        </button>
        <h1 style={{ margin: 0, fontSize: 22, color: 'var(--text)', display: 'flex', alignItems: 'center', gap: 10 }}>
          <meta.Icon size={20} /> {meta.label}
        </h1>
        <button
          type="button"
          onClick={() => onNavigate && onNavigate(targetView)}
          style={{
            marginLeft: 'auto',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            background: 'var(--accent)',
            color: '#fff',
            border: 'none',
            borderRadius: 'var(--radius-sm)',
            padding: '8px 14px',
            fontSize: 13,
            fontWeight: 500,
            cursor: 'pointer',
          }}
        >
          <TargetIcon size={15} /> {targetLabel}
        </button>
      </div>
      <p style={{ color: 'var(--text-muted)', fontSize: 13, margin: 0 }}>
        {periodLabel}
        {data?.aggregate ? ` · taux ${data.aggregate.fillPct ?? '—'}%` : ''}
      </p>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {pill(onlyMissing, 'À compléter', null, () => setOnlyMissing(true))}
        {pill(!onlyMissing, 'Tous les jours', null, () => setOnlyMissing(false))}
        {availableScopes.length > 1 ? (
          <span style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
            {pill(scope === 'self', 'Moi', null, () => setScope('self'))}
            {availableScopes.includes('team')
              ? pill(scope === 'team', 'Mon équipe', Users, () => setScope('team'))
              : null}
            {availableScopes.includes('all')
              ? pill(scope === 'all', 'Tout le monde', Globe, () => setScope('all'))
              : null}
          </span>
        ) : null}
      </div>

      {error ? (
        <div style={{ ...card, borderColor: 'var(--danger)', color: 'var(--danger)', fontSize: 13 }}>
          {error}
        </div>
      ) : null}

      {loading && !data ? (
        <div style={{ color: 'var(--text-muted)' }}>Chargement…</div>
      ) : null}

      {data ? (
        <>
          <section style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <h2 style={sectionTitle}>
              Ma situation
              {data.personal?.metric ? (
                <span style={{ color: 'var(--text-muted)', fontWeight: 400, fontSize: 13 }}>
                  · {fmtHours(
                    meta.kind === 'scheduling'
                      ? data.personal.metric.allocatedHours
                      : data.personal.metric.loggedHours,
                  )}h /{' '}
                  {fmtHours(data.personal.metric.capacityHours)}h
                </span>
              ) : null}
            </h2>
            <div style={card}>
              {data.personal?.metric ? (
                <DayList
                  days={myDays}
                  kind={meta.kind}
                  emptyLabel={emptyLabel}
                  onOpenDay={
                    onOpenDay ? (date) => onOpenDay(targetView, date, null) : undefined
                  }
                />
              ) : (
                <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>
                  Aucune fiche collaborateur n'est liée à votre compte.
                </div>
              )}
            </div>
          </section>

          {scope !== 'self' ? (
            <section style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <h2 style={sectionTitle}>
                <Users size={16} />
                {scope === 'all' ? 'Tout le monde' : 'Mon équipe'}
                <span style={{ color: 'var(--text-muted)', fontWeight: 400, fontSize: 13 }}>
                  · {teamRows.length} personne{teamRows.length > 1 ? 's' : ''}
                </span>
              </h2>
              {teamRows.length === 0 ? (
                <div style={{ ...card, color: 'var(--success)', fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <CheckCircle2 size={15} /> {emptyLabel}
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {teamRows.map((r) => (
                    <div key={r.personId} style={{ ...card, display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ color: 'var(--text)', fontWeight: 600, fontSize: 13 }}>
                          {r.firstName} {r.lastName}
                        </span>
                        {r.jobTitle ? (
                          <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>{r.jobTitle}</span>
                        ) : null}
                        <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text-muted)' }}>
                          {r.metric?.fillPct ?? '—'}%
                        </span>
                      </div>
                      <DayList
                        days={r.days}
                        kind={meta.kind}
                        emptyLabel={emptyLabel}
                        onOpenDay={
                          onOpenDay
                            ? (date) => onOpenDay(targetView, date, r.personId)
                            : undefined
                        }
                      />
                    </div>
                  ))}
                </div>
              )}
            </section>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
